import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  IAiModel,
  ICreateConversation,
  IGenerateTitle,
  IListChat,
  IMessageInChat,
  generateTitle,
} from '../../api/chat';
import {
  useChatStream,
  useCreateConversation,
  useGetAssetPresignUrl,
  useSendMessageInChat,
  useStopConversation,
} from '../../hooks/useChatStream';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useToggleStore } from '../../hooks/useTogger';
export function useChatLogic() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { createConversation } = useCreateConversation();
  const { sendMessage } = useChatStream();
  const { sendMessage: sendMessageInChat } = useSendMessageInChat();
  const { getAssetPresignUrl } = useGetAssetPresignUrl();
  const { toggle } = useToggleStore();
  const { stopConversation } = useStopConversation();
  
  // State for AI loading status
  const [isAiLoading, setIsAiLoading] = useState(false);
  
  // Refs for accumulating AI responses and storing new conversation ID
  const accumulatedResponse = useRef<string>('');
  const idConversationNew = useRef<string>('');

  // Callback for handling streaming chunks from AI
  const handleChunkCallback = useCallback(
    async (
      chunk: string, 
      setItems: React.Dispatch<React.SetStateAction<IMessageInChat[]>>,
      setListChatPage: React.Dispatch<React.SetStateAction<IListChat[]>>,
      dataGenerateTitle: IGenerateTitle | null = null
    ) => {
      const jsonStr = chunk.replace(/^data: /, '');
      try {
        const data = JSON.parse(jsonStr);
        if (data.chunk) {
          // Accumulate the chunks
          accumulatedResponse.current += data.chunk;

          setItems((prevItems) => {
            const lastItem = prevItems[prevItems.length - 1];
            return [
              ...prevItems.slice(0, -1),
              {
                ...lastItem,
                content: {
                  ...lastItem.content,
                  text: lastItem.content.text + data.chunk,
                },
              },
            ];
          });
        }
        if (data.status === 'completed' || data.status === 'stopped') {
          if (dataGenerateTitle) {
            const dataGenerateTitleMatch = {
              ...dataGenerateTitle,
              history_chat: [
                ...dataGenerateTitle.history_chat,
                accumulatedResponse.current,
              ], // Add the complete response
            };
            // Reset the accumulated response for next conversation
            accumulatedResponse.current = '';

            const title = await generateTitle(dataGenerateTitleMatch);
            const newConversation: IListChat = {
              title: title,
              id: dataGenerateTitle.conversation_id,
              aiModelId: dataGenerateTitle.aiModelId || '',
              userId: dataGenerateTitle.userId || '',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            setListChatPage((prev) => [newConversation, ...prev]);
            idConversationNew.current = dataGenerateTitle?.conversation_id || '';
          }
          queryClient.invalidateQueries({ queryKey: ['me'] });
          setIsAiLoading(false);
        }
      } catch (error) {
        console.error('Error parsing chunk:', error);
        setIsAiLoading(false);
      }
    },
    [queryClient]
  );

  // Function to handle sending a new message
  const handleSendMessage = async (
    newMessage: string,
    items: IMessageInChat[],
    setItems: React.Dispatch<React.SetStateAction<IMessageInChat[]>>,
    setListChatPage: React.Dispatch<React.SetStateAction<IListChat[]>>,
    conversationIdCurrent: string | null,
    aiModelCurrent: IAiModel | null,
    setAiModelCurrent: React.Dispatch<React.SetStateAction<IAiModel | null>>,
    aiModels: IAiModel[] | undefined,
    listChatPage: IListChat[],
    profileId: string | undefined
  ) => {
    try {
      setIsAiLoading(true);
      if (newMessage.trim()) {
        if (items.length === 0) {
          // Create new conversation
          const aiModel = aiModelCurrent || aiModels?.[0] || null;
          if (!aiModelCurrent) {
            setAiModelCurrent(aiModel);
          }
          const payload: ICreateConversation = {
            content: {
              text: newMessage,
            },
            aiModelId: aiModel?.id || '',
          };
          const res = await createConversation(payload);
          navigate(`/?conversationId=${res.id}`, { replace: true });
          const idMessageUser = uuidv4();
          const newMessages: IMessageInChat[] = [
            {
              id: idMessageUser,
              senderRole: 'USER',
              content: {
                text: newMessage,
              },
              conversationId: res.id,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: uuidv4(),
              senderRole: 'SYSTEM',
              content: {
                text: '',
              },
              conversationId: res.id,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ];
          setItems([...items, ...newMessages]);

          const dataGenerateTitle: IGenerateTitle = {
            conversation_id: res.id.toString(),
            access_token_user: localStorage.getItem('accessToken')!,
            llm_name: `${aiModel?.aiModelProvider.name}`,
            model: `${aiModel?.code}`,
            history_chat: [newMessage],
            aiModelId: aiModel?.id || '',
            userId: profileId || '',
          };
          
          await sendMessage(
            {
              question: {
                text: newMessage,
              },
              llm_name: `${aiModel?.aiModelProvider.name}`,
              model: `${aiModel?.code}`,
              conversation_id: `${res.id}`,
              access_token_user: localStorage.getItem('accessToken')!,
            },
            (chunk) => handleChunkCallback(chunk, setItems, setListChatPage, dataGenerateTitle)
          );
        } else {
          // Send message in existing conversation
          const idMessageUser = uuidv4();
          const newMessages: IMessageInChat[] = [
            {
              id: idMessageUser,
              senderRole: 'USER',
              content: {
                text: newMessage,
              },
              conversationId: conversationIdCurrent || idConversationNew.current,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: uuidv4(),
              senderRole: 'SYSTEM',
              content: {
                text: '',
              },
              conversationId: conversationIdCurrent || idConversationNew.current,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ];
          setItems([...items, ...newMessages]);
          
          const res = await sendMessageInChat({
            conversationId: conversationIdCurrent || idConversationNew.current,
            message: {
              content: {
                text: newMessage,
              },
            },
          });
          
          // Update message ID after receiving response
          const idMessageSystem = items.find(
            (item) => item.id === idMessageUser
          );
          if (idMessageSystem) {
            idMessageSystem.id = res.id;
          }

          let aiModel = aiModelCurrent;
          if (!aiModel) {
            const conversation = listChatPage.find(
              (item) => item.id?.toString() === conversationIdCurrent
            );
            if (conversation) {
              aiModel =
                aiModels?.find((item) => item.id === conversation.aiModelId) ||
                null;
              setAiModelCurrent(aiModel);
            }
          }
          
          sendMessage(
            {
              question: {
                text: newMessage,
              },
              llm_name: `${aiModel?.aiModelProvider.name}`,
              model: `${aiModel?.code}`,
              conversation_id: `${
                conversationIdCurrent || idConversationNew.current
              }`,
              access_token_user: localStorage.getItem('accessToken')!,
            },
            (chunk) => handleChunkCallback(chunk, setItems, setListChatPage, null)
          );
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setIsAiLoading(false);
    }
  };
  // Function to handle sending a new message
  const handleSendMessageWithFile = async (
    newMessage: string,
    file: File,
    fileUrl: string,
    items: IMessageInChat[],
    setItems: React.Dispatch<React.SetStateAction<IMessageInChat[]>>,
    setListChatPage: React.Dispatch<React.SetStateAction<IListChat[]>>,
    conversationIdCurrent: string | null,
    aiModelCurrent: IAiModel | null,
    setAiModelCurrent: React.Dispatch<React.SetStateAction<IAiModel | null>>,
    aiModels: IAiModel[] | undefined,
    listChatPage: IListChat[],
    profileId: string | undefined
  ) => {
    try {
      setIsAiLoading(true);
      if (newMessage.trim()) {
          const idMessageUser = uuidv4();
          const idMessageSystem = uuidv4();
          const newMessages: IMessageInChat[] = [
            {
              id: idMessageUser,
              senderRole: 'USER',
              content: {
                text: newMessage,
                files: [{
                  url: fileUrl,
                  type: file.type,
                  description: file.name,
                }],
              },
              conversationId: idMessageSystem,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: uuidv4(),
              senderRole: 'SYSTEM',
              content: {
                text: '',
              },
              conversationId: idMessageSystem,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ];
          setItems([...items, ...newMessages]);
          const assetPresignUrl = await getAssetPresignUrl({
            fileName: uuidv4(),
            fileType: 'image',
            fileExt: file.name.split('.').pop() || '',
          });
          const request = await fetch(assetPresignUrl.presignUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
          });

          if (request.status != 200) {
            toast.error('Failed to upload image');
            throw new Error('Failed to upload image');
          }

        if (items.length === 0) {
          // Create new conversation
          const aiModel = aiModelCurrent || aiModels?.[0] || null;
          if (!aiModelCurrent) {
            setAiModelCurrent(aiModel);
          }
          const payload: ICreateConversation = {
            content: {
              text: newMessage,
              files: [{
                url: assetPresignUrl.url,
                type: 'image',
                description: file.name,
              }],
            },
            aiModelId: aiModel?.id || '',
          };
          const res = await createConversation(payload);
          navigate(`/?conversationId=${res.id}`, { replace: true });

          const dataGenerateTitle: IGenerateTitle = {
            conversation_id: res.id.toString(),
            access_token_user: localStorage.getItem('accessToken')!,
            llm_name: `${aiModel?.aiModelProvider.name}`,
            model: `${aiModel?.code}`,
            history_chat: [newMessage],
            aiModelId: aiModel?.id || '',
            userId: profileId || '',
          };
          
          await sendMessage(
            {
              question: {
                text: newMessage,
                img_urls: [assetPresignUrl.url],
              },
              llm_name: `${aiModel?.aiModelProvider.name}`,
              model: `${aiModel?.code}`,
              conversation_id: `${res.id}`,
              access_token_user: localStorage.getItem('accessToken')!,
            },
            (chunk) => handleChunkCallback(chunk, setItems, setListChatPage, dataGenerateTitle)
          );
        } else {
          const res = await sendMessageInChat({
            conversationId: conversationIdCurrent || idConversationNew.current,
            message: {
              content: {
                text: newMessage,
                files: [{
                  url: assetPresignUrl.url,
                  type: 'image',
                  description: file.name,
                }],
              },
            },
          });
          
          // Update message ID after receiving response
          const idMessageSystem = items.find(
            (item) => item.id === idMessageUser
          );
          if (idMessageSystem) {
            idMessageSystem.id = res.id;
          }

          let aiModel = aiModelCurrent;
          if (!aiModel) {
            const conversation = listChatPage.find(
              (item) => item.id?.toString() === conversationIdCurrent
            );
            if (conversation) {
              aiModel =
                aiModels?.find((item) => item.id === conversation.aiModelId) ||
                null;
              setAiModelCurrent(aiModel);
            }
          }
          
          sendMessage(
            {
              question: {
                text: newMessage,
                img_urls: [assetPresignUrl.url],
              },
              llm_name: `${aiModel?.aiModelProvider.name}`,
              model: `${aiModel?.code}`,
              conversation_id: `${
                conversationIdCurrent || idConversationNew.current
              }`,
              access_token_user: localStorage.getItem('accessToken')!,
            },
            (chunk) => handleChunkCallback(chunk, setItems, setListChatPage, null)
          );
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setIsAiLoading(false);
    }
  };

  const handleChooseConversation = (
    conversation: IListChat,
    aiModels: IAiModel[] | undefined,
    setConversationIdCurrent: React.Dispatch<React.SetStateAction<string | null>>,
    setAiModelCurrent: React.Dispatch<React.SetStateAction<IAiModel | null>>
  ) => {
    setConversationIdCurrent(conversation.id);
    setAiModelCurrent(
      aiModels?.find((item) => item.id === conversation.aiModelId) || null
    );
    navigate(`/?conversationId=${conversation.id}`, { replace: true });
    if(window.innerWidth < 768) {
      toggle();
    } 
  };

  const newConversation = (
    setConversationIdCurrent: React.Dispatch<React.SetStateAction<string | null>>,
    setItems: React.Dispatch<React.SetStateAction<IMessageInChat[]>>
  ) => {
    setConversationIdCurrent(null);
    setItems([]);
    navigate(`/`, { replace: true });
  };

  const handleStopConversation = async (conversationId: string) => {
    await stopConversation({
      conversation_id: conversationId,
      access_token_user: localStorage.getItem('accessToken')!
    });
    setIsAiLoading(false);
  };

  return {
    handleSendMessage,
    handleSendMessageWithFile,
    handleChooseConversation,
    newConversation,
    isAiLoading,
    idConversationNew: idConversationNew.current,
    handleStopConversation,
  };
} 